/**
 * DOM recording policy for the browser profile (docs/sandbox.md §3.1–3.2):
 * which element attributes load resources, which calls insert nodes, and
 * which form fields hold sensitive values. Pure data, bundled into the
 * guest; changing what gets recorded is a data edit.
 */

/** The interface name per tag, for event targets like `HTMLScriptElement.src`. */
export const ELEMENT_INTERFACES: Readonly<Record<string, string>> = {
  a: "HTMLAnchorElement",
  audio: "HTMLAudioElement",
  body: "HTMLBodyElement",
  button: "HTMLButtonElement",
  div: "HTMLDivElement",
  embed: "HTMLEmbedElement",
  form: "HTMLFormElement",
  frame: "HTMLFrameElement",
  head: "HTMLHeadElement",
  html: "HTMLHtmlElement",
  iframe: "HTMLIFrameElement",
  img: "HTMLImageElement",
  input: "HTMLInputElement",
  link: "HTMLLinkElement",
  meta: "HTMLMetaElement",
  object: "HTMLObjectElement",
  script: "HTMLScriptElement",
  select: "HTMLSelectElement",
  source: "HTMLSourceElement",
  span: "HTMLSpanElement",
  style: "HTMLStyleElement",
  textarea: "HTMLTextAreaElement",
  video: "HTMLVideoElement",
};

export interface ResourceAttribute {
  attribute: string;
  /** Loads even while the element isn't in the document (e.g. `new Image().src`). */
  detached: boolean;
}

/** Attributes that make an element fetch a URL, per tag. */
export const RESOURCE_ATTRIBUTES: Readonly<Record<string, readonly ResourceAttribute[]>> = {
  img: [{ attribute: "src", detached: true }],
  script: [{ attribute: "src", detached: false }],
  iframe: [{ attribute: "src", detached: false }],
  frame: [{ attribute: "src", detached: false }],
  link: [{ attribute: "href", detached: false }],
  video: [
    { attribute: "src", detached: true },
    { attribute: "poster", detached: true },
  ],
  audio: [{ attribute: "src", detached: true }],
  source: [{ attribute: "src", detached: false }],
  embed: [{ attribute: "src", detached: false }],
  object: [{ attribute: "data", detached: false }],
};

/** `<link rel>` values that fetch their `href`. */
export const FETCHING_LINK_RELS: readonly string[] = [
  "stylesheet",
  "preload",
  "prefetch",
  "modulepreload",
  "icon",
  "manifest",
  "preconnect",
  "dns-prefetch",
];

/** Methods that insert their node arguments into the tree. */
export const INSERTION_METHODS: readonly string[] = [
  "appendChild",
  "insertBefore",
  "replaceChild",
  "append",
  "prepend",
  "before",
  "after",
  "replaceWith",
  "replaceChildren",
  "insertAdjacentElement",
];

/** Members that parse HTML into the tree. */
export const HTML_SINKS: readonly string[] = [
  "innerHTML",
  "outerHTML",
  "insertAdjacentHTML",
  "write",
  "writeln",
];

/** Input types whose value is sensitive. */
export const SENSITIVE_INPUT_TYPES: readonly string[] = ["password", "email", "tel"];

/**
 * Substrings of a field's name, id, or autocomplete token that mark its
 * value as sensitive (payment and credential fields).
 */
export const SENSITIVE_FIELD_TOKENS: readonly string[] = [
  "card",
  "cc-",
  "ccnum",
  "cvc",
  "cvv",
  "csc",
  "pass",
  "pwd",
  "email",
  "iban",
  "ssn",
  "otp",
];
